/**
 * Per-component model registry: defines which ONNX variants exist for each
 * stage of the pipeline. Updated as quantization variants get produced.
 *
 * The UI builds dropdowns from this, and main.ts loads the selected variant
 * via ORT-Web.
 */

export type Variant = "fp32" | "fp16" | "int8" | "int4";

export interface ComponentSpec {
  /** Programmatic id, matches `onnx/out/<id>_<variant>.onnx`. */
  id: string;
  /** Display name (kept under ~12 chars). */
  name: string;
  /** Reserved -- currently unused; kept for future inline help. */
  desc?: string;
  /** Declared variants. Actual on-disk sizes are read from the HEAD probe
   *  in main.ts:availableVariants and displayed in the dropdown there;
   *  hardcoding numbers here was constantly drifting from reality. */
  variants: Variant[];
  /** Default variant. */
  default: Variant;
}

export const COMPONENTS: ComponentSpec[] = [
  { id: "rmbg",        name: "BG removal",   desc: "", variants: ["fp32", "int8"],                 default: "int8" },
  { id: "dinov3",      name: "Image encoder",desc: "", variants: ["fp16", "int4"],                 default: "int4" },
  { id: "vae_encoder", name: "VAE encoder",  desc: "", variants: ["fp32", "fp16", "int8", "int4"], default: "fp16" },
  { id: "flow",        name: "Denoiser",     desc: "", variants: ["fp32", "fp16", "int4"],         default: "int4" },
  { id: "octree_step", name: "Octree",       desc: "", variants: ["fp32", "fp16", "int8", "int4"], default: "fp16" },
  { id: "gs_decoder",  name: "GS decoder",   desc: "", variants: ["fp32", "fp16", "int4"],         default: "fp32" },
];

/** Where to fetch ONNX models from. Defaults to /onnx/ (Vite dev / local
 *  files); override with VITE_MODELS_BASE_URL at build time for R2 / CDN
 *  hosting. The base must end with a slash; trailing slash is stripped if
 *  forgotten. Filenames are `<id>_<variant>.onnx`. */
const MODELS_BASE = (() => {
  const raw = (import.meta as any).env?.VITE_MODELS_BASE_URL ?? "/onnx/";
  return raw.endsWith("/") ? raw : raw + "/";
})();

export interface ModelUrlOpts {
  /** Only meaningful for `id === "flow"`. Picks the un-chunked export, which
   *  ORT-Web's WebGPU optimizer can fuse into the flash-attention kernel. */
  flash?: boolean;
}

export function modelUrl(id: string, variant: Variant, opts?: ModelUrlOpts): string {
  const suffix = (id === "flow" && opts?.flash) ? "_flash" : "";
  return `${MODELS_BASE}${id}_${variant}${suffix}.onnx`;
}
